import {
  Handshake,
  HeartPulse,
  Headset,
  Pill,
  Settings2,
  Stethoscope,
  Users,
  type LucideIcon,
} from "lucide-react";

export type Portal =
  | "staff"
  | "pharmacist"
  | "customer-success"
  | "provider"
  | "sales"
  | "patient"
  | "engineering";

export type PortalInfo = {
  label: string;
  eyebrow: string;
  description: string;
  href: string;
  letter: string;
  accent: string;
  icon: LucideIcon;
};

export const PREFERRED_PORTAL_KEY = "preferredPortal";

export const PORTAL_INFO: Record<Portal, PortalInfo> = {
  patient: {
    label: "Patient",
    eyebrow: "For patients",
    description: "Refills, billing, shipping and live order tracking — care in one place.",
    href: "/patient",
    letter: "P",
    accent: "#74443d",
    icon: HeartPulse,
  },
  provider: {
    label: "Provider",
    eyebrow: "For providers",
    description: "Patient roster, chart notes and e-prescribing for your practice.",
    href: "/provider",
    letter: "P",
    accent: "#08745d",
    icon: Stethoscope,
  },
  pharmacist: {
    label: "Pharmacist",
    eyebrow: "Clinical",
    description: "Prescription tasks, the pharmacist queue, status review, and compliance tools.",
    href: "/pharmacist",
    letter: "R",
    accent: "#0b634e",
    icon: Pill,
  },
  "customer-success": {
    label: "Customer Success",
    eyebrow: "Internal",
    description: "Patient worklists, team capacity, support tasks, and state mapping.",
    href: "/customer-success",
    letter: "C",
    accent: "#bc6b59",
    icon: Headset,
  },
  sales: {
    label: "Sales",
    eyebrow: "Internal",
    description: "Orders, practices, field coaching, team activity, and sales resources.",
    href: "/sales",
    letter: "S",
    accent: "#8a5a44",
    icon: Handshake,
  },
  staff: {
    label: "Staff",
    eyebrow: "Internal",
    description: "Live KPIs, the order queue, shipping and the full user directory.",
    href: "/staff",
    letter: "S",
    accent: "#673d36",
    icon: Users,
  },
  engineering: {
    label: "Engineering",
    eyebrow: "Internal",
    description: "Engineering recommendations, delivery signals, and platform health insights.",
    href: "/staff/engineering/recommendations",
    letter: "E",
    accent: "#51616f",
    icon: Settings2,
  },
};

export function isPortal(value: unknown): value is Portal {
  return typeof value === "string" && value in PORTAL_INFO;
}

export function getAccessiblePortals(
  user: { portals?: readonly string[] | null } | null | undefined
): Portal[] {
  if (!user) return [];

  // `/me/` is the single source of truth for both membership and order.
  return Array.from(new Set((user.portals ?? []).filter(isPortal)));
}

export function setPreferredPortal(portal: Portal) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(PREFERRED_PORTAL_KEY, portal);
  }
}

export function clearPreferredPortal() {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(PREFERRED_PORTAL_KEY);
  }
}

export function canAccessPortal(
  user: { portals?: readonly string[] | null } | null | undefined,
  portal: Portal
): boolean {
  if (!user) return false;
  return (user.portals ?? []).includes(portal);
}
